import {
  Badge,
  Bank,
  Button,
  Col,
  Copy,
  DataHeader,
  Descriptions,
  Divider,
  Flex,
  Grid,
  Page,
  Row,
  Space,
  Table,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { ArrowRightOutlined, CuToman, PhRDownloadSimple } from "@payping/icons";
import { requestPublicApis, settlementApis, uploadV2Apis } from "@payping/api";
import { FiltersType, GridifyConditionEnum, createQuery } from "@payping/utils/api";
import { useNavigate, useParams } from "react-router-dom";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { ReactElement, useState } from "react";
import { SettlementDownloadListType, SettlementType } from "@/pages/Settlement/Report/types";
import { generateExcel } from "@/utils/excelGenerator";
import { StatusObj } from "@/pages/Settlement/Report/Settlement/types";
import { ColumnsType, TablePaginationConfig } from "@payping/web-components/components/Table/types";
import { RequestStatusEnum, SettlementFullDetails, TransferTypeEnum } from "@payping/api/app/api/cashOutTypes";
import ReportSettlementDetails from "@/pages/Settlement/Report/Settlement/Detail";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

const SettlementRecordList = () => {
  const { t } = useAppTranslation();
  const { code } = useParams<{ code: string }>() as { code: string };
  const navigate = useNavigate();
  const [settlementPageNumber, setSettlementPageNumber] = useState<number>(1);
  const [settlementPageSize, setSettlementPageSize] = useState<number>(50);
  const { lg } = Grid.useBreakpoint();

  const [settlementDetails, setSettlementDetails] = useState<
    { show: false; data: undefined } | { show: true; data: SettlementFullDetails }
  >({ show: false, data: undefined });

  const { value: requestList, loading: settlementLoading } = useMessagedAsync(
    async () => {
      const filter: FiltersType = [
        {
          code: [GridifyConditionEnum.Equal, code],
        },
      ];
      return requestPublicApis.requestsListWithQueryGet(undefined, undefined, undefined, createQuery(filter));
    },
    [code],
    { useProgress: true },
  );

  const { value: settlementList } = useMessagedAsync(
    async () => settlementApis.settlementsListWithRequestCodeWithQueryWithGetAllGet(code, 1, 50, "status desc"),
    [],
    { useProgress: true },
  );

  const settlementPagination: TablePaginationConfig = {
    "data-test": "report-request-settlement-list-pagination",
    onChange: (page, newSize): void => {
      setSettlementPageNumber(page);
      setSettlementPageSize(newSize);
    },
    onShowSizeChange: (_currentPage, newSize): void => {
      setSettlementPageNumber(1);
      setSettlementPageSize(newSize);
    },
    pageNumber: settlementPageNumber,
    pageSize: settlementPageSize,
    showTotal: (total, range): string => t("showTotal", { range: `${range[0]}-${range[1]}`, total }),
    total: settlementList?.count,
    hideOnSinglePage: true,
  };

  const statusMaps = (statusValue: string) => {
    if (statusValue) {
      const statusObj = StatusObj.filter((stat) => stat.no === parseInt(statusValue, 10));
      if (statusObj[0]) return statusObj[0].locale;
    }
    return "unknown";
  };

  const statusTag = (statusMap: string) => {
    if (statusMap) {
      const statusObj = StatusObj.filter((stat) => stat.no === parseInt(statusMap, 10));
      if (statusObj[0]) return statusObj[0].locale;
    }
    return t("unclear");
  };
  const settlementColumns: ColumnsType<SettlementFullDetails> = [
    {
      dataIndex: "fullName",
      key: "fullName",
      render(fullName: string) {
        return fullName || "-";
      },
      title: t("settlement.report.name"),
    },
    {
      dataIndex: "iban",
      key: "iban",
      render(shaba: string) {
        return (
          <Space>
            <Copy text={shaba || ""}>{shaba || "_"}</Copy>
            <Bank.Icon showTooltip iban={shaba || "-"} />
          </Space>
        );
      },
      title: t("settlement.report.shaba"),
      width: 300,
    },
    {
      dataIndex: "code",
      key: "code",
      render(_, tableData) {
        return <Copy text={tableData.code || ""}>{tableData.code}</Copy>;
      },
      title: t("settlement.trackingCode"),
    },
    {
      dataIndex: "status",
      key: "status",
      render(_status: string) {
        return <span>{t(`settlement.status.${statusMaps(_status)}`)}</span>;
      },
      title: t("status"),
    },
    {
      dataIndex: "amount",
      // @ts-ignore: To be checked later editable
      editable: true,
      key: "amount",
      render(amount: string) {
        return (
          <Flex align="center">
            <span>{formatLocalizedNumber({ value: Number(amount) })}</span>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("settlement.report.amount"),
      width: 230,
    },
    {
      dataIndex: "submittedTime",
      key: "submittedTime",
      render(submittedTime: string) {
        return <span>{toJalali({ date: submittedTime, toFormat: dateFormats.ui.jalaliDateTime }).string}</span>;
      },
      title: t("settlement.requestDate"),
    },
    {
      dataIndex: "wage",
      key: "wage",
      render(wage: string) {
        return (
          <Flex align="center">
            <span>{formatLocalizedNumber({ value: Number(wage) })}</span>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("settlement.wage"),
    },
    {
      dataIndex: "bankTransferReference",
      key: "bankTransferReference",
      render(bankTransferReference: string) {
        return bankTransferReference || "-";
      },
      title: t("settlement.bankTransferReference"),
    },
    {
      dataIndex: "transferType",
      key: "transferType",
      render(transferType: number) {
        if (transferType === TransferTypeEnum.ACH) return t("settlement.type.paya");
        if (transferType === TransferTypeEnum.NORMAL) return t("settlement.type.acountToAcount");
        return t("settlement.type.auto");
      },
      title: t("settlement.type.transfer"),
    },
  ];

  const [, downloadList] = useMessagedAsyncFn(
    async (result = settlementList?.data) => {
      if (result) {
        const excelData = result.map((data: SettlementDownloadListType) => [
          toJalali({ date: data.submittedTime, toFormat: dateFormats.ui.jalaliDateTime }).string,
          data.code,
          data.fullName,
          data.iban,
          data.clientRefId,
          data.persianBankName,
          data.bankTransferReference,
          data.amount,
          data.wage,
          t(`settlement.status.${statusTag(data.status?.toString() || "")}`),
          data.description,
        ]);
        generateExcel([
          [
            t("settlement.requestDate"),
            t("settlement.trackingCode"),
            t("fullName"),
            t("shaba"),
            t("settlement.clientRefId"),
            t("settlement.destinationBank"),
            t("settlement.bankTransferReference"),
            t("settlement.amount"),
            t("settlement.wage"),
            t("status"),
            t("description"),
          ],
          [...excelData],
        ]);
      }
    },
    [code, settlementList?.data],
    undefined,
    { useProgress: true },
  );

  const [{ loading: downloadReportLoading }, downloadReport] = useMessagedAsyncFn(async (row) => {
    const newWindow = window.open(undefined, "_blank");
    const path = await uploadV2Apis.settlementReportPdfWithSettlementCodeGet(row.code);
    newWindow?.location?.assign?.(path);
  }, []);
  const settlementActions = (_: any, record: any): ReactElement[] => {
    const { code: recordCode } = record;
    const dataList = settlementList?.data;
    const result = (dataList || []).find((i) => i.code === recordCode);
    return [
      result ? (
        <Button
          key="detail"
          type="link"
          onClick={() => {
            setSettlementDetails({
              data: result,
              show: true,
            });
          }}
          className="tw-inline"
        >
          {t("common.details")}
        </Button>
      ) : (
        <span> - </span>
      ),
      (Number(record.status) === RequestStatusEnum.Final || Number(record.status) === RequestStatusEnum.Schedule) &&
      record.bankTransferReference ? (
        <Button
          onClick={() => downloadReport(record)}
          type="link"
          loading={downloadReportLoading}
          className="tw-inline"
        >
          {t("settlement.downloadReport")}
        </Button>
      ) : (
        <span> - </span>
      ),
    ];
  };

  return (
    <Page>
      <Flex vertical className="tw-w-full tw-gap-4 xl:tw-gap-12">
        <Space direction={"vertical"} size={"large"} className={"tw-w-full"}>
          <Row gutter={[8, 32]}>
            <Col xs={24}>
              <Space size={"middle"} className={"tw-align-center tw-flex"}>
                <Button
                  type={"text"}
                  icon={<ArrowRightOutlined width={14} height={14} />}
                  shape={"circle"}
                  onClick={() => navigate(-1)}
                ></Button>
                <Typography.Title className={"!tw-m-0"} level={5}>
                  {t("settlement.reportDetail.title", {
                    date: toJalali({
                      date: requestList?.data?.[0]?.submittedTime,
                      toFormat: dateFormats.ui.jalaliDate,
                    }).string,
                  })}
                </Typography.Title>
              </Space>
            </Col>
            <Col xs={24}>
              <Descriptions className={!lg ? "tw-flex tw-flex-row" : ""} column={{ xs: 1, xl: 5 }}>
                <Descriptions.Item>
                  <Space className={!lg ? "tw-flex tw-w-full tw-justify-between" : ""}>
                    <Typography.Text type={"secondary"}>{t("settlement.report.list.requestCode")}</Typography.Text>
                    <Copy text={requestList?.data?.[0]?.code || ""}>{requestList?.data?.[0]?.code}</Copy>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item>
                  <Space className={!lg ? "tw-flex tw-w-full tw-justify-between" : ""}>
                    <Typography.Text type={"secondary"}>{t("settlement.report.list.totalSettlement")}</Typography.Text>
                    <Flex align="center">
                      <Typography.Text>{`${formatLocalizedNumber({ value: requestList?.data?.[0]?.totalAmount ?? 0 })}`}</Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item>
                  <Space className={!lg ? "tw-flex tw-w-full tw-justify-between" : ""}>
                    <Typography.Text type={"secondary"}>{t("settlement.report.list.totalWage")}</Typography.Text>
                    <Flex align="center">
                      <Typography.Text>{`${formatLocalizedNumber({ value: requestList?.data?.[0]?.totalWage ?? 0 })}`}</Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item>
                  <Space className={!lg ? "tw-flex tw-w-full tw-justify-between" : ""}>
                    <Typography.Text type={"secondary"}>{t("settlement.report.list.totalAmount")}</Typography.Text>
                    <Flex align="center">
                      <Typography.Text>{`${formatLocalizedNumber({
                        value: (requestList?.data?.[0]?.totalAmount || 0) + (requestList?.data?.[0]?.totalWage || 0),
                      })}`}</Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item>
                  <div className={!lg ? "tw-flex tw-w-full tw-justify-between" : ""}>
                    <Typography.Text className={"tw-ml-1"} type={"secondary"}>
                      {t("settlement.report.list.status")}
                    </Typography.Text>
                    <Space>
                      <Badge
                        dot={true}
                        status={"success"}
                        text={`${requestList?.data?.[0]?.succeededCount || 0} ${t(
                          "settlement.reportDetail.status.success",
                        )}`}
                      />
                      {"/"}
                      <Badge
                        dot={true}
                        status={"warning"}
                        text={`${requestList?.data?.[0]?.inProgressCount || 0} ${t(
                          "settlement.reportDetail.status.inProgress",
                        )}`}
                      />
                      {"/"}
                      <Badge
                        dot={true}
                        status={"error"}
                        text={`${requestList?.data?.[0]?.failedCount || 0} ${t(
                          "settlement.reportDetail.status.error",
                        )}`}
                      />
                    </Space>
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Divider className={"!tw-m-0"} />
          </Row>
          <DataHeader
            title={
              lg ? (
                <Typography.Title level={5}>
                  {t("settlement.reportDetail.nameList", { name: requestList?.data?.[0]?.title })}
                </Typography.Title>
              ) : null
            }
            filters={{
              downloadButton: (
                <Button
                  className="tw-w-full"
                  icon={<PhRDownloadSimple width={14} height={14} />}
                  onClick={() => {
                    downloadList();
                  }}
                  key={1}
                >
                  {t("downloadExcel")}
                </Button>
              ),
            }}
          />
          <Table<SettlementType>
            hasIdColumn
            actions={settlementActions}
            dataSource={settlementList?.data}
            columns={settlementColumns}
            pagination={settlementPagination}
            loading={settlementLoading}
          />
        </Space>
        {settlementDetails.show && (
          <ReportSettlementDetails
            open={settlementDetails.show}
            data={settlementDetails.data}
            onClose={() => setSettlementDetails({ data: undefined, show: false })}
          />
        )}
      </Flex>
    </Page>
  );
};

export default SettlementRecordList;
